import { useState } from "react";
import { useResetPassword } from "ra-supabase-core";
import { Form, required, useNotify, useTranslate } from "ra-core";
import { Layout } from "@/components/supabase/layout";
import type { FieldValues, SubmitHandler } from "react-hook-form";
import { TextInput } from "@/components/admin/text-input";
import { Button } from "@/components/ui/button";
import { getHashQueryParams } from "@/components/supabase/hashQuery";

interface FormData {
  email: string;
}

/**
 * O link do e-mail vale uma vez só e por uma hora. Quando ele já morreu, o
 * auth-callback.html manda pra cá com o motivo — antes a pessoa era despejada
 * no login sem nada escrito, e a leitura óbvia era "o reset não funciona".
 */
const AVISO_POR_MOTIVO: Record<string, string> = {
  otp_expired:
    "Link expirado ou já utilizado. Solicite um novo link abaixo.",
  access_denied:
    "Link expirado ou já utilizado. Solicite um novo link abaixo.",
  tokens_ausentes:
    "Link incompleto. Solicite um novo link abaixo e abra-o diretamente no navegador.",
};

const avisoDaUrl = (): string | null => {
  const motivo = getHashQueryParams().get("motivo");
  if (!motivo) return null;
  return (
    AVISO_POR_MOTIVO[motivo] ??
    "Não foi possível abrir o link do e-mail. Solicite um novo link abaixo."
  );
};

export const ForgotPasswordPage = () => {
  const [loading, setLoading] = useState(false);
  const [aviso] = useState(avisoDaUrl);

  const notify = useNotify();
  const translate = useTranslate();
  const [, { mutateAsync: resetPassword }] = useResetPassword();

  const submit = async (values: FormData) => {
    try {
      setLoading(true);
      const redirectTo = `${window.location.origin}/auth-callback.html`;
      await resetPassword({
        email: values.email,
        redirectTo,
      });
    } catch (error: any) {
      notify(
        typeof error === "string"
          ? error
          : typeof error === "undefined" || !error.message
            ? "ra.auth.sign_in_error"
            : error.message,
        {
          type: "warning",
          messageArgs: {
            _:
              typeof error === "string"
                ? error
                : error && error.message
                  ? error.message
                  : undefined,
          },
        },
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <div className="flex flex-col space-y-2 text-center">
        <h1 className="text-2xl font-bold tracking-[-0.02em] text-marinho">
          {translate("ra-supabase.reset_password.forgot_password", {
            _: "Forgot password?",
          })}
        </h1>
        <p>
          {translate("ra-supabase.reset_password.forgot_password_details", {
            _: "Enter your email to receive a reset password link.",
          })}
        </p>
      </div>
      {aviso ? (
        <p
          role="alert"
          className="rounded-[7px] border border-alerta/30 bg-alerta-fundo px-3 py-2 text-sm text-alerta"
        >
          {aviso}
        </p>
      ) : null}
      <Form<FormData>
        className="space-y-8"
        onSubmit={submit as SubmitHandler<FieldValues>}
      >
        <TextInput
          source="email"
          label={translate("ra.auth.email", {
            _: "Email",
          })}
          autoComplete="email"
          validate={required()}
        />
        <Button type="submit" className="cursor-pointer" disabled={loading}>
          {translate("ra-supabase.auth.reset_password", {
            _: "Reset password",
          })}
        </Button>
      </Form>
    </Layout>
  );
};

ForgotPasswordPage.path = "forgot-password";
